import { type FC, createRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { formatFileSize } from "@/shared/utils/formatFileSize";

interface FileUploadZoneProps {
  onFileSelect: (file: File) => void;
  accept?: string;
  maxSize?: number; // in bytes
  loading?: boolean;
  disabled?: boolean;
  text?: string;
  formatText?: string;
  sizeText?: string;
  buttonText?: string;
}

export const FileUploadZone: FC<FileUploadZoneProps> = ({
  onFileSelect,
  accept = "*",
  maxSize,
  loading = false,
  disabled = false,
  text,
  formatText,
  sizeText,
  buttonText,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [drag, setDrag] = useState(false);
  const inputRef = createRef<HTMLInputElement>();

  const handleFile = useCallback(
    (file: File) => {
      if (maxSize && file.size > maxSize) {
        return;
      }
      onFileSelect(file);
    },
    [maxSize, onFileSelect]
  );

  const handleClick = () => {
    if (!disabled && !loading) {
      inputRef.current?.click();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    // Reset input to allow selecting the same file again
    event.target.value = "";
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled && !loading) {
      setDrag(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDrag(false);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDrag(false);

    if (disabled || loading) return;

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const displayText = text ?? t("shared.fileUploadZone.text");
  const displayButtonText = buttonText ?? t("shared.fileUploadZone.button");
  const displaySizeText =
    sizeText ||
    (maxSize
      ? t("shared.fileUploadZone.maxSize", {
          size: formatFileSize(maxSize),
        })
      : "");

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        border: "2px dashed",
        borderColor: drag ? theme.palette.primary.main : theme.palette.divider,
        borderRadius: 2,
        padding: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        // Только токены темы: захардкоженный светлый фон в тёмной теме давал
        // белую плашку с белым текстом.
        backgroundColor: drag
          ? theme.palette.action.selected
          : theme.palette.action.hover,
        transition: "all 0.2s ease-in-out",
        cursor: disabled || loading ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        position: "relative",
        minHeight: 200,
      }}
      onClick={handleClick}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{
          position: "absolute",
          opacity: 0,
          width: 0,
          height: 0,
          pointerEvents: "none",
        }}
        onChange={handleChange}
        disabled={disabled || loading}
      />

      {loading ? (
        <CircularProgress size={48} />
      ) : (
        <>
          <FileUploadIcon
            sx={{
              fontSize: 64,
              color: drag
                ? theme.palette.primary.main
                : theme.palette.text.secondary,
              transition: "color 0.2s ease-in-out",
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ textAlign: "center" }}
            >
              {displayText}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {[formatText, displaySizeText].filter(Boolean).join(", ")}
            </Typography>

            <Button
              variant="outlined"
              color="inherit"
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
              disabled={disabled || loading}
              sx={{ mt: 1 }}
            >
              {displayButtonText}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
